/* ═══════════════════════════════════════════════════════════════
   FirmActivityPanel — admin-gated viewer for cross-user activity.

   Shows two surfaces:

     1. Last seen per user — quick "is this beta tester still using
        the app?" rollup. Sorted by most recent.

     2. Activity feed — most recent N events, filterable by user
        and action type. Auto-refreshes every 30s.

   Permission gate: rendered ONLY when the caller is satisfied that
   the current user has `audit_log.view_all`. Server enforces the
   same check on every request — UI gating is for UX, server gating
   is for safety.

   Retention: 30 days, server-side. Older rows are pruned by the
   in-process cron in ec2-api/server.js.
   ═══════════════════════════════════════════════════════════════ */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { listActivity, lastSeenPerUser, type ActivityRow } from '../services/activityLogger';
import { TeamService } from '../services/teamService';
import type { TeamMember } from '../../shared/types';
import { SearchableSelect } from './SearchableSelect';
import { rem } from '../lib/rem';

interface Props {
  toast: (msg: string, isError?: boolean) => void;
}

const ACTION_LABELS: Record<string, { label: string; color: string }> = {
  login: { label: 'Signed in', color: 'var(--color-success)' },
  logout: { label: 'Signed out', color: 'var(--color-text-muted)' },
  'page.view': { label: 'Opened page', color: 'var(--color-primary)' },
  'project.create': { label: 'Created project', color: 'var(--color-success)' },
  'project.update': { label: 'Edited project', color: 'var(--color-primary)' },
  'project.delete': { label: 'Deleted project', color: 'var(--color-danger)' },
  'spec.publish': { label: 'Published spec', color: 'var(--color-success)' },
  'spec.update': { label: 'Edited spec', color: 'var(--color-primary)' },
  'spec.draft_save': { label: 'Saved spec draft', color: 'var(--color-smart)' },
  'spec.delete': { label: 'Deleted spec', color: 'var(--color-danger)' },
  'forecast.save': { label: 'Saved forecast', color: 'var(--color-smart)' },
  'time.save': { label: 'Logged time', color: 'var(--color-primary)' },
  'invoice.create': { label: 'Created invoice', color: 'var(--color-success)' },
  'invoice.send': { label: 'Sent invoice', color: 'var(--color-primary)' },
  'invoice.paid': { label: 'Marked paid', color: 'var(--color-success)' },
  'invoice.void': { label: 'Voided invoice', color: 'var(--color-danger)' },
  'invoice.update': { label: 'Edited invoice', color: 'var(--color-primary)' },
  'invoice.delete': { label: 'Deleted invoice', color: 'var(--color-danger)' },
};

function actionMeta(a: string): { label: string; color: string } {
  return ACTION_LABELS[a] || { label: a, color: 'var(--color-text-muted)' };
}

function timeAgo(iso: string): string {
  const ts = new Date(iso).getTime();
  const diff = Date.now() - ts;
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const days = Math.floor(hr / 24);
  if (days < 7) return `${days}d ago`;
  const weeks = Math.floor(days / 7);
  if (weeks < 4) return `${weeks}w ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
}

export function FirmActivityPanel({ toast }: Props) {
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [lastSeen, setLastSeen] = useState<{ userId: string; action: string; createdAt: string }[]>(
    []
  );
  const [feed, setFeed] = useState<ActivityRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterUserId, setFilterUserId] = useState<string>('');
  const [filterAction, setFilterAction] = useState<string>('');
  const [refreshing, setRefreshing] = useState(false);

  const memberById = useMemo(() => {
    const m = new Map<string, TeamMember>();
    for (const x of members) m.set(x.id, x);
    return m;
  }, [members]);

  const refresh = useCallback(async () => {
    setRefreshing(true);
    try {
      const [seen, rows] = await Promise.all([
        lastSeenPerUser().catch(() => []),
        listActivity({
          limit: 200,
          filterUserId: filterUserId || undefined,
          actionFilter: filterAction || undefined,
        }).catch(() => []),
      ]);
      setLastSeen(seen);
      setFeed(rows);
    } catch (err: any) {
      toast('Activity log load failed: ' + (err?.message || String(err)), true);
    } finally {
      setRefreshing(false);
      setLoading(false);
    }
  }, [filterUserId, filterAction, toast]);

  // Initial load + members
  useEffect(() => {
    TeamService.list()
      .then((m) => setMembers(m.filter((x) => x.active)))
      .catch(() => setMembers([]));
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Auto-refresh every 30s.
  useEffect(() => {
    const t = setInterval(refresh, 30_000);
    return () => clearInterval(t);
  }, [refresh]);

  // Build sorted last-seen rows: include even members with no events
  // (they show "never" — the whole point of the leaderboard is to
  // surface dormant beta testers).
  const lastSeenRows = useMemo(() => {
    const seenByUser = new Map(lastSeen.map((s) => [s.userId, s]));
    return members
      .map((m) => {
        const s = seenByUser.get(m.id);
        return {
          memberId: m.id,
          name: m.name,
          email: m.email,
          action: s?.action || null,
          createdAt: s?.createdAt || null,
        };
      })
      .sort((a, b) => {
        if (!a.createdAt && !b.createdAt) return a.name.localeCompare(b.name);
        if (!a.createdAt) return 1;
        if (!b.createdAt) return -1;
        return b.createdAt.localeCompare(a.createdAt);
      });
  }, [lastSeen, members]);

  const filteredFeed = feed; // server already applied filterUserId / actionFilter

  // Distinct action types from the visible feed for the filter dropdown.
  const actionOptions = useMemo(() => {
    const set = new Set<string>(feed.map((r) => r.action));
    return Array.from(set).sort();
  }, [feed]);

  if (loading) {
    return (
      <div
        style={{
          padding: 'var(--space-4)',
          color: 'var(--tm)',
          fontSize: 'calc(var(--font-size-base) * 12 / 13)',
        }}
      >
        Loading activity…
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
      {/* ── Last seen leaderboard ── */}
      <div
        style={{
          background: 'var(--sf)',
          border: '1px solid var(--bd)',
          borderRadius: 'var(--radius-lg)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            padding: 'var(--space-3) var(--space-4)',
            borderBottom: '1px solid var(--bd)',
            display: 'flex',
            alignItems: 'center',
            gap: rem(10),
          }}
        >
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 'var(--font-size-md)' }}>
              Last seen — by user
            </div>
            <div
              style={{
                fontSize: 'var(--font-size-sm)',
                color: 'var(--tm)',
                // eslint-disable-next-line sf/no-raw-px -- 2px, below the >4 conversion threshold
                marginTop: 2,
              }}
            >
              Most recent activity per active member. Sorted newest first. Idle members appear at
              the bottom.
            </div>
          </div>
          <button
            onClick={refresh}
            disabled={refreshing}
            style={{
              padding: `${rem(5)} var(--space-3)`,
              borderRadius: rem(5),
              border: '1px solid var(--bd)',
              background: 'transparent',
              color: 'var(--tm)',
              fontSize: 'var(--font-size-sm)',
              cursor: refreshing ? 'wait' : 'pointer',
            }}
          >
            {refreshing ? '…' : '↻ Refresh'}
          </button>
        </div>
        <div style={{ maxHeight: rem(260), overflow: 'auto' }}>
          {lastSeenRows.length === 0 && (
            <div
              style={{
                padding: 'var(--space-4)',
                fontSize: 'calc(var(--font-size-base) * 12 / 13)',
                color: 'var(--tm)',
              }}
            >
              No active members.
            </div>
          )}
          {lastSeenRows.map((r) => {
            const idle =
              !r.createdAt || Date.now() - new Date(r.createdAt).getTime() > 7 * 86400000;
            return (
              <div
                key={r.memberId}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-3)',
                  padding: 'var(--space-2) var(--space-4)',
                  borderTop: '1px solid var(--bd)',
                  opacity: r.createdAt ? 1 : 0.55,
                }}
              >
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <span
                    style={{
                      fontSize: 'calc(var(--font-size-base) * 12 / 13)',
                      fontWeight: 600,
                      color: 'var(--tx)',
                    }}
                  >
                    {r.name}
                  </span>
                  {r.email && (
                    <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--tm)' }}>
                      {r.email}
                    </span>
                  )}
                </div>
                <span
                  style={{
                    fontSize: 'var(--font-size-xs)',
                    padding: `2px var(--space-2)`,
                    borderRadius: 'var(--radius-lg)',
                    background: r.action
                      ? 'color-mix(in srgb, var(--color-primary) 12%, transparent)'
                      : 'var(--sa)',
                    color: r.action ? actionMeta(r.action).color : 'var(--tm)',
                    fontWeight: 600,
                  }}
                >
                  {r.action ? actionMeta(r.action).label : 'never seen'}
                </span>
                <span
                  style={{
                    fontSize: 'var(--font-size-sm)',
                    color: idle ? 'var(--tm)' : 'var(--tx)',
                    width: rem(96),
                    textAlign: 'right',
                    fontFamily: "'JetBrains Mono', monospace",
                  }}
                >
                  {r.createdAt ? timeAgo(r.createdAt) : '—'}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Activity feed ── */}
      <div
        style={{
          background: 'var(--sf)',
          border: '1px solid var(--bd)',
          borderRadius: 'var(--radius-lg)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            padding: 'var(--space-3) var(--space-4)',
            borderBottom: '1px solid var(--bd)',
            display: 'flex',
            alignItems: 'center',
            gap: rem(10),
            flexWrap: 'wrap',
          }}
        >
          <div style={{ flex: 1, minWidth: rem(200) }}>
            <div style={{ fontWeight: 700, fontSize: 'var(--font-size-md)' }}>Activity feed</div>
            <div
              style={{
                fontSize: 'var(--font-size-sm)',
                color: 'var(--tm)',
                // eslint-disable-next-line sf/no-raw-px -- 2px, below the >4 conversion threshold
                marginTop: 2,
              }}
            >
              Last 200 events. 30-day server-side retention.
            </div>
          </div>
          <div style={{ width: rem(200) }}>
            <SearchableSelect
              value={filterUserId}
              onChange={(v) => setFilterUserId(v)}
              options={[
                { value: '', label: 'All users' },
                ...members.map((m) => ({ value: m.id, label: m.name })),
              ]}
              placeholder="Filter user"
            />
          </div>
          <div style={{ width: rem(200) }}>
            <SearchableSelect
              value={filterAction}
              onChange={(v) => setFilterAction(v)}
              options={[
                { value: '', label: 'All actions' },
                ...actionOptions.map((a) => ({ value: a, label: actionMeta(a).label })),
              ]}
              placeholder="Filter action"
            />
          </div>
        </div>

        <div style={{ maxHeight: rem(480), overflow: 'auto' }}>
          {filteredFeed.length === 0 && (
            <div
              style={{
                padding: 'var(--space-4)',
                fontSize: 'calc(var(--font-size-base) * 12 / 13)',
                color: 'var(--tm)',
                textAlign: 'center',
              }}
            >
              No activity in the selected window.
            </div>
          )}
          {filteredFeed.map((r) => {
            const m = r.userId ? memberById.get(r.userId) : null;
            const meta = actionMeta(r.action);
            return (
              <div
                key={r.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-3)',
                  padding: `${rem(6)} var(--space-4)`,
                  borderTop: '1px solid var(--bd)',
                  fontSize: 'calc(var(--font-size-base) * 12 / 13)',
                }}
              >
                <span
                  style={{
                    fontFamily: "'JetBrains Mono', monospace",
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--tm)',
                    width: rem(90),
                  }}
                >
                  {timeAgo(r.createdAt)}
                </span>
                <span
                  style={{
                    width: rem(140),
                    color: 'var(--tx)',
                    fontWeight: 600,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {m?.name || (r.userId ? 'Unknown user' : 'System')}
                </span>
                <span
                  style={{
                    fontSize: 'var(--font-size-xs)',
                    padding: `2px var(--space-2)`,
                    borderRadius: 'var(--radius-lg)',
                    background: `color-mix(in srgb, ${meta.color} 15%, transparent)`,
                    color: meta.color,
                    fontWeight: 600,
                    width: rem(130),
                    textAlign: 'center',
                  }}
                >
                  {meta.label}
                </span>
                <span
                  style={{
                    flex: 1,
                    color: 'var(--tm)',
                    fontSize: 'var(--font-size-sm)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {summarizeMetadata(r)}
                </span>
                {r.appVersion && (
                  <span
                    style={{
                      fontFamily: "'JetBrains Mono', monospace",
                      fontSize: 'calc(var(--font-size-base) * 9 / 13)',
                      color: 'var(--tm)',
                      opacity: 0.6,
                      width: rem(50),
                      textAlign: 'right',
                    }}
                  >
                    {r.appVersion}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function summarizeMetadata(r: ActivityRow): string {
  if (!r.metadata) return r.entityType ? r.entityType : '';
  const md = r.metadata;
  const bits: string[] = [];
  if (md.name) bits.push(md.name);
  if (md.invoiceNumber) bits.push(md.invoiceNumber);
  if (md.projectNumber) bits.push(md.projectNumber);
  if (md.sectionNumber) bits.push(md.sectionNumber);
  if (md.page) bits.push(`page: ${md.page}`);
  if (typeof md.hours === 'number') bits.push(`${md.hours}h`);
  if (md.dayDate) bits.push(md.dayDate);
  if (md.date) bits.push(md.date);
  if (typeof md.total === 'number')
    bits.push(`$${md.total.toLocaleString(undefined, { maximumFractionDigits: 0 })}`);
  if (md.status) bits.push(md.status);
  return bits.join(' · ');
}
